import { Controller } from '@hotwired/stimulus';
import L from 'leaflet';
import { copyToClipboard } from '../../utils/copy_to_clipboard';
import 'leaflet/dist/leaflet.css';
import { translate } from '../../utils/translation';

export default class extends Controller {
  static targets = ['container'];
  static values = {
    coordinates: Object,
    url: String,
  };

  static MAP_CONFIG = {
    ZOOM: 15,
    MIN_ZOOM: 9,
    MAX_ZOOM: 17,
    TILE_LAYER: 'https://osm.hasznaltautocdn.com/osm/{z}/{x}/{y}.png',
    ICON_SIZE: [38, 62],
    ICON_ANCHOR: [19, 48],
    ICON_COLOR: '#ff4200',
  };

  connect() {
    this.initializeMap();
  }

  disconnect() {
    this.cleanupMap();
  }

  initializeMap() {
    const { lat, long } = this.coordinatesValue;

    this.createMap(lat, long);
    this.addTileLayer();
    this.addMarker(lat, long);
    this.setupScrollWheelZoom();
  }

  createMap(lat, long) {
    this.map = L.map(this.containerTarget).setView([lat, long], this.constructor.MAP_CONFIG.ZOOM);
  }

  addTileLayer() {
    L.tileLayer(this.constructor.MAP_CONFIG.TILE_LAYER, {
      attribution: `© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>, <a href="${this.urlValue}" target="_blank">Használtautó.hu</a>`,
      minZoom: this.constructor.MAP_CONFIG.MIN_ZOOM,
      maxZoom: this.constructor.MAP_CONFIG.MAX_ZOOM,
    }).addTo(this.map);
  }

  addMarker(lat, long) {
    L.marker([lat, long], {
      icon: this.createLocationIcon(),
    }).addTo(this.map);
  }

  setupScrollWheelZoom = () => {
    this.map.scrollWheelZoom.disable();
    this.map.on('click', () => this.map.scrollWheelZoom.enable());
    this.map.on('mouseout', () => this.map.scrollWheelZoom.disable());
  };

  createLocationIcon() {
    const svgIcon = this.generateLocationSvg();
    const svgUrl = `data:image/svg+xml;base64,${btoa(svgIcon)}`;

    return L.icon({
      iconUrl: svgUrl,
      iconSize: this.constructor.MAP_CONFIG.ICON_SIZE,
      iconAnchor: this.constructor.MAP_CONFIG.ICON_ANCHOR,
    });
  }

  generateLocationSvg() {
    const { ICON_COLOR } = this.constructor.MAP_CONFIG;

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${ICON_COLOR}" width="38" height="62">
        <path d="M12,12c.55,0,1.02-.2,1.41-.59.39-.39.59-.86.59-1.41s-.2-1.02-.59-1.41-.86-.59-1.41-.59-1.02.2-1.41.59-.59.86-.59,1.41.2,1.02.59,1.41c.39.39.86.59,1.41.59ZM12,22c-2.68-2.28-4.69-4.4-6.01-6.36s-1.99-3.77-1.99-5.44c0-2.5.8-4.49,2.41-5.98,1.61-1.48,3.47-2.23,5.59-2.23s3.98.74,5.59,2.23,2.41,3.48,2.41,5.98c0,1.67-.66,3.48-1.99,5.44s-3.33,4.08-6.01,6.36Z"/>
      </svg>
    `;
  }

  cleanupMap() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  onAddressClick(event) {
    void copyToClipboard(event.params.location, translate('pages.ad_view.map.copy_location.success_toast'));
  }

  onBusinessHoursAccordionStateChange() {
    this.containerTarget.classList.add('map__container--fade-out');
  }

  onBusinessHoursAccordionStateChanged() {
    this.containerTarget.classList.remove('map__container--fade-out');
    this.containerTarget.classList.add('map__container--fade-in');

    this.map.invalidateSize();
  }
}
